import { useState } from "react";
import { useContent } from "@/hooks/useContent";
import EmptyStateCard from "./EmptyStateCard";
import { cn } from "@/lib/utils";
import { Scale, BriefcaseBusiness } from "lucide-react";
import Workspace from "@/models/workspace/Workspace";
import SourceSelectionDialog from "../../components/dialogs/SourceSelectionDialog";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import PromptLibraryDialog from "@/pages/chat/components/dialogs/PromptLibraryDialog";
import { WorkspaceSelectionDialog } from "../workspace-selection-dialog";
import { WorkspaceContextCapBanner } from "../WorkspaceContextCapBanner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@capacit-com/lara-law-shared-ui";
import PromptLibraryIcon from "../../components/prompt-library/PromptLibraryIcon";
import { useWorkspaceDialogSync } from "../../hooks/useWorkspaceDialogSync";

type Props = {
  workspaces: Workspace[];
  isLoadingWorkspaces: boolean;
  onPromptSelect?: (promptText: string) => void;
};

const EmptyStateFeatureCards = ({
  workspaces,
  isLoadingWorkspaces,
  onPromptSelect,
}: Props) => {
  const { t } = useContent();
  const [legalSourcesPopoverOpen, setLegalSourcesPopoverOpen] = useState(false);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const { workspaceContext, filters } = useNewConversationFiltersStore();

  const numberOfWorkspaces = Object.values(workspaceContext).length;
  const numberOfSources = Object.values(filters).flat().length;
  const hasWorkspaceContext = numberOfWorkspaces > 0;
  const hasLegalSources = numberOfSources > 0;
  const canAddSources = !hasWorkspaceContext;
  const canAddWorkspace = !hasLegalSources;

  const {
    selectedItems,
    handleSelectItem,
    handleDeselectWorkspace,
    handleSelectAllWorkspaces,
    commitSelection,
    resetSelection,
  } = useWorkspaceDialogSync({
    workspaceDialogOpen: contextDialogOpen,
    workspaces,
  });

  const handleContextDialogClose = () => {
    commitSelection();
    setContextDialogOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 w-full max-w-4xl">
      {/* Legal Sources Card */}
      <div className="flex-1 flex flex-col">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "flex-1 flex flex-col",
                  !canAddSources && "opacity-50"
                )}
              >
                <EmptyStateCard
                  icon={
                    <Scale className="size-6 text-muted-foreground dark:text-white" />
                  }
                  title={t("chat.emptyState.cards.legalSources.title")}
                  description={t(
                    "chat.emptyState.cards.legalSources.description"
                  )}
                  onClick={
                    canAddSources
                      ? () => setLegalSourcesPopoverOpen(true)
                      : undefined
                  }
                  className={!canAddSources ? "cursor-not-allowed" : undefined}
                />
              </div>
            </TooltipTrigger>
            {!canAddSources && (
              <TooltipContent>
                <p className="max-w-[250px]">
                  {t("chat.context.cannotAddSourceMessage")}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <SourceSelectionDialog
          open={legalSourcesPopoverOpen}
          onOpenChange={setLegalSourcesPopoverOpen}
        />
      </div>

      {/* Own Sources Card */}
      <div className="flex-1 flex flex-col">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "flex-1 flex flex-col",
                  !canAddWorkspace && "opacity-50"
                )}
              >
                <EmptyStateCard
                  icon={
                    <BriefcaseBusiness className="size-6 text-muted-foreground dark:text-white" />
                  }
                  title={t("chat.emptyState.cards.ownSources.title")}
                  description={t(
                    "chat.emptyState.cards.ownSources.description"
                  )}
                  onClick={
                    canAddWorkspace
                      ? () => setContextDialogOpen(true)
                      : undefined
                  }
                  className={
                    !canAddWorkspace ? "cursor-not-allowed" : undefined
                  }
                />
              </div>
            </TooltipTrigger>
            {!canAddWorkspace && (
              <TooltipContent>
                <p className="max-w-[250px]">
                  {t("chat.context.cannotAddWorkspaceMessage")}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <WorkspaceSelectionDialog
          open={contextDialogOpen}
          onOpenChange={(open) => {
            if (!open) resetSelection();
            setContextDialogOpen(open);
          }}
          title={t("chat.context.selectWorkspaceTitle")}
          workspaces={workspaces}
          isLoading={isLoadingWorkspaces}
          emptyMessage={t("chat.context.noWorkspaceFilesFound")}
          selectedItems={selectedItems.map((s) => s.item)}
          onSelectItem={handleSelectItem}
          onDeselectWorkspace={handleDeselectWorkspace}
          onSelectAllWorkspaces={handleSelectAllWorkspaces}
          onConfirm={handleContextDialogClose}
          confirmLabel={t("chat.context.confirm")}
          showSearch
          explorerDisabled={hasLegalSources}
          warningBanner={
            <div className="flex shrink-0 flex-col gap-2">
              {hasLegalSources ? (
                <div className="p-3 bg-warning/10 border border-warning/30 rounded-md">
                  <p className="text-sm text-warning ">
                    {t("chat.context.alreadyHasSourcesMessage")}
                  </p>
                </div>
              ) : null}
              <WorkspaceContextCapBanner selectedItems={selectedItems} />
            </div>
          }
        />
      </div>

      {/* Prompts Card */}
      <div className="flex-1 flex flex-col">
        <EmptyStateCard
          onClick={() => setPromptLibraryOpen(true)}
          icon={
            <PromptLibraryIcon className="size-8 text-muted-foreground dark:text-white" />
          }
          title={t("chat.emptyState.cards.prompts.title")}
          description={t("chat.emptyState.cards.prompts.description")}
        />
      </div>
      <PromptLibraryDialog
        open={promptLibraryOpen}
        onOpenChange={setPromptLibraryOpen}
        onPromptSelect={onPromptSelect}
      />
    </div>
  );
};

export default EmptyStateFeatureCards;
